import { useState } from 'react'
import type { AttributeDefinition, BoardFinding, BoardMember } from '../api/client'
import { ramp } from './Avatar'

/*
  Whether this board is any good, and where it is thin.

  Two pictures of the same thing the validator already knows. Nothing here is a new
  judgement: the eight checks are BoardValidator's own, by name, and the coverage bars are
  the competence numbers the seats already carry. The screen used to state the failures as
  a paragraph of warnings and say nothing at all when everything passed, which meant the
  owner learned the board was fine only by the absence of complaint.
*/

/**
 * The eight checks BoardValidator runs, in the order they matter.
 *
 * Hard-coded rather than derived from the findings, because findings only arrive for checks
 * that failed. A meter built from failures alone would show three segments on a bad board
 * and none on a good one, which is exactly backwards.
 */
const CHECKS: { key: string; label: string; blurb: string }[] = [
  { key: 'competence_coverage', label: 'Coverage',  blurb: 'Somebody on this board is deep in each discipline it needs.' },
  { key: 'critical_gap',        label: 'No gaps',   blurb: 'No discipline is left with nobody who understands it.' },
  { key: 'trade_depth',         label: 'The trade', blurb: 'At least one seat knows the work itself, not just business.' },
  { key: 'grounding',           label: 'Grounded',  blurb: 'Somebody has run something the size of yours.' },
  { key: 'dissent_capacity',    label: 'Dissent',   blurb: 'At least one seat will argue with the rest.' },
  { key: 'disposition_spread',  label: 'Spread',    blurb: 'The seats do not all think the same way.' },
  { key: 'echo_chamber',        label: 'No echo',   blurb: 'No two seats are near-copies of each other.' },
  { key: 'immovability',        label: 'Movable',   blurb: 'Somebody will change their mind when the numbers do.' },
]

export function BoardRating({ findings }: { findings: BoardFinding[] }) {
  const [open, setOpen] = useState<string | null>(null)

  const failed = new Map(findings.map(f => [f.check, f]))
  const passed = CHECKS.filter(c => !failed.has(c.key)).length

  const shown = open ? CHECKS.find(c => c.key === open) : null
  const finding = shown ? failed.get(shown.key) : undefined

  return (
    <div className="rating">
      <div className="rating__head">
        <span className="rating__score">
          Passes <strong>{passed}</strong> of {CHECKS.length}
        </span>
        <span className="rating__hint">Tap a mark to see what it checks</span>
      </div>

      <div className="rating__bar" role="list">
        {CHECKS.map(c => {
          const f = failed.get(c.key)
          const state = !f ? 'pass' : f.isBlocking ? 'block' : 'warn'
          return (
            <button
              key={c.key}
              type="button"
              role="listitem"
              className={`rating__seg rating__seg--${state}${open === c.key ? ' is-open' : ''}`}
              aria-label={`${c.label}: ${f ? 'not met' : 'met'}`}
              onClick={() => setOpen(open === c.key ? null : c.key)}
            >
              <span className="rating__seglabel">{c.label}</span>
            </button>
          )
        })}
      </div>

      {shown && (
        <p className={`rating__say${finding ? ' rating__say--fail' : ''}`}>
          {finding ? finding.message : shown.blurb}
        </p>
      )}
    </div>
  )
}

/*
  Where the board is deep, and where it is not.

  One bar per discipline, stacked by seat so the colour says who provides the depth. A short
  bar is the point of the chart: it is the same thing critical_gap says in a sentence, except
  you can see it without reading, and you can see the near-misses the check does not fire on.
*/
export function Coverage({
  members, definitions,
}: {
  members: BoardMember[]
  definitions: AttributeDefinition[]
}) {
  const axes = definitions
    .filter(d => d.category === 'competence')
    .sort((a, b) => a.sortOrder - b.sortOrder)

  if (axes.length === 0 || members.length === 0) return null

  // The scale is the deepest single discipline on this board, so the tallest bar always
  // fills. An absolute 0-to-50 scale made every bar short on a young board and told the
  // owner their board was weak when it was only new.
  const totals = axes.map(d =>
    members.reduce((sum, m) => sum + (m.attributes.competence[d.key] ?? 0), 0))
  const most = Math.max(...totals, 1)

  return (
    <div className="cov">
      {axes.map((d, i) => (
        <div className="cov__row" key={d.key}>
          <span className="cov__name">{d.displayName}</span>

          <span className="cov__track">
            {members.map(m => {
              const v = m.attributes.competence[d.key] ?? 0
              if (v === 0) return null
              return (
                <span
                  key={m.id}
                  className="cov__part"
                  style={{ ...ramp(m.colorRamp), width: `${(v / most) * 100}%` }}
                  title={`${m.displayName}: ${v}`}
                />
              )
            })}
          </span>

          <span className={`cov__total${totals[i]! === 0 ? ' cov__total--none' : ''}`}>
            {totals[i] === 0 ? 'nobody' : totals[i]}
          </span>
        </div>
      ))}
    </div>
  )
}
